/**
 * KioskCheckIn.js — Futuristic kiosk check-in screen
 * Customer-facing · Floating hexagon background · No names shown on success
 */
import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  FaSearch, FaCheckCircle, FaUsers, FaClock,
  FaTimesCircle, FaShieldAlt, FaLock, FaSignOutAlt,
} from "react-icons/fa";
import { Colors } from "./branding";
import { useAppConfig } from "./AppContext";
import SixersLogo    from "./Sixers_Logo.png";
import bg2           from "./Background images/2.jpg";
import API_BASE from "./apiConfig";
const BASE = API_BASE;

const T = {
  blue:   Colors.primaryBlue || "#034e7b",
  yellow: Colors.logoYellow  || "#f7ce0b",
  gray:   "#8899aa",
  font:   "'Exo 2', 'Segoe UI', system-ui, sans-serif",
  disp:   "'Rajdhani', sans-serif",
};

// ─── Global styles & keyframes ────────────────────────────────────────────────
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Rajdhani:wght@500;600;700&family=Exo+2:wght@300;400;500;600;700;800&display=swap');

  @keyframes ki_hex    { 0%,100%{transform:translateY(0) rotate(0deg);opacity:0.13}   50%{transform:translateY(-18px) rotate(6deg);opacity:0.30} }
  @keyframes ki_scan   { 0%{transform:translateY(-100%)} 100%{transform:translateY(100vh)} }
  @keyframes ki_fadeUp { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:translateY(0)} }
  @keyframes ki_stripe { from{transform:scaleX(0);transform-origin:left} to{transform:scaleX(1);transform-origin:left} }
  @keyframes ki_flash  { 0%{opacity:0;transform:scale(0.88)} 16%{opacity:1;transform:scale(1.04)} 78%{opacity:1;transform:scale(1)} 100%{opacity:0;transform:scale(0.97)} }
  @keyframes ki_spin   { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
  @keyframes ki_dot    { 0%,100%{box-shadow:0 0 5px rgba(74,222,128,0.5)} 50%{box-shadow:0 0 14px rgba(74,222,128,0.95)} }
  @keyframes ki_pulse  { 0%,100%{filter:drop-shadow(0 0 18px rgba(247,206,11,0.40))} 50%{filter:drop-shadow(0 0 38px rgba(247,206,11,0.80))} }
  @keyframes ki_ring   { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }

  .ki_batch { transition: background 0.18s, transform 0.15s, box-shadow 0.15s; }
  .ki_batch:hover { background: rgba(255,255,255,0.16) !important; transform: translateY(-2px); box-shadow: 0 8px 28px rgba(247,206,11,0.12) !important; }

  .ki_card { transition: transform 0.15s, box-shadow 0.15s; }
  .ki_card:not(.ki_done):hover { transform: translateY(-5px); box-shadow: 0 18px 44px rgba(0,0,0,0.45) !important; border-color: rgba(247,206,11,0.55) !important; }

  .ki_search::placeholder { color: rgba(255,255,255,0.25); }
  .ki_search:focus { border-color: rgba(247,206,11,0.55) !important; box-shadow: 0 0 0 3px rgba(247,206,11,0.08), 0 4px 24px rgba(0,0,0,0.30) !important; }
`;

// ─── Floating hexagon grid ─────────────────────────────────────────────────────
const HEXES = [
  { x:4,  y:8,  s:1.4, d:0.0 },
  { x:17, y:54, s:0.8, d:1.2 },
  { x:61, y:9,  s:1.2, d:0.7 },
  { x:80, y:61, s:1.6, d:0.5 },
  { x:44, y:77, s:0.7, d:1.8 },
  { x:10, y:75, s:1.0, d:1.0 },
  { x:87, y:27, s:0.9, d:1.5 },
  { x:52, y:43, s:0.6, d:2.1 },
  { x:33, y:31, s:0.55, d:0.8 },
  { x:70, y:83, s:0.8, d:1.3 },
];
function HexGrid() {
  return (
    <div style={{ position:"absolute", inset:0, pointerEvents:"none", overflow:"hidden" }}>
      {HEXES.map((h, i) => (
        <div key={i} style={{
          position:"absolute", left:`${h.x}%`, top:`${h.y}%`,
          width:64*h.s, height:64*h.s,
          animation:`ki_hex ${4+h.d}s ease-in-out ${h.d}s infinite`,
        }}>
          <svg viewBox="0 0 100 100" style={{ width:"100%", height:"100%" }}>
            <polygon points="50,2 93,26 93,74 50,98 7,74 7,26"
              fill="none" stroke="rgba(247,206,11,0.48)" strokeWidth="1.5"/>
          </svg>
        </div>
      ))}
    </div>
  );
}

function fmtTime(t) {
  if (!t) return "";
  const [h, m] = t.split(":");
  const hr = parseInt(h);
  return `${hr%12||12}:${m} ${hr>=12?"PM":"AM"}`;
}

// ─── Stat pill ────────────────────────────────────────────────────────────────
function Pill({ icon:Icon, label, value, color, bg, borderColor }) {
  return (
    <div style={{
      display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center",
      padding:"16px 32px", borderRadius:16,
      background:bg,
      border:`1px solid ${borderColor||color+"44"}`,
      minWidth:120,
      boxShadow:`0 4px 24px ${color}18`,
      fontFamily:T.font,
      backdropFilter:"blur(14px)", WebkitBackdropFilter:"blur(14px)",
    }}>
      <Icon size={19} style={{ color, marginBottom:7 }}/>
      <div style={{ fontFamily:T.disp, fontSize:32, fontWeight:700, color, lineHeight:1, letterSpacing:"0.05em" }}>{value}</div>
      <div style={{ fontFamily:T.disp, fontSize:10, fontWeight:700, color, opacity:0.80, textTransform:"uppercase", letterSpacing:"0.14em", marginTop:5 }}>{label}</div>
    </div>
  );
}

// ─── Student card ─────────────────────────────────────────────────────────────
function StudentCard({ student, onCheckin, loading }) {
  const initials  = student.student_name?.split(" ").map(w=>w[0]).slice(0,2).join("") || "?";
  const alreadyIn = !!student.already_checked_in;

  return (
    <div
      className={`ki_card${alreadyIn?" ki_done":""}`}
      onClick={() => !alreadyIn && !loading && onCheckin(student)}
      style={{
        display:"flex", flexDirection:"column", alignItems:"center",
        padding:"26px 22px", borderRadius:20,
        background:  alreadyIn ? "rgba(16,185,129,0.10)" : "rgba(255,255,255,0.07)",
        border:      alreadyIn ? "2px solid rgba(16,185,129,0.55)" : "1.5px solid rgba(255,255,255,0.18)",
        backdropFilter:"blur(18px)", WebkitBackdropFilter:"blur(18px)",
        cursor:      alreadyIn ? "default" : "pointer",
        boxShadow:   "0 6px 28px rgba(0,0,0,0.28)",
        position:"relative", minWidth:160, maxWidth:200,
        fontFamily:T.font, WebkitTapHighlightColor:"transparent",
      }}>

      {/* Already-checked badge */}
      {alreadyIn && (
        <div style={{
          position:"absolute", top:10, right:10,
          background:"#10b981", borderRadius:"50%",
          width:28, height:28,
          display:"flex", alignItems:"center", justifyContent:"center",
          boxShadow:"0 2px 12px rgba(16,185,129,0.55)",
        }}>
          <FaCheckCircle size={16} style={{ color:"white" }}/>
        </div>
      )}

      {/* Photo */}
      <div style={{
        width:100, height:100, borderRadius:"50%", overflow:"hidden",
        marginBottom:14,
        border:`3px solid ${alreadyIn ? "rgba(16,185,129,0.55)" : "rgba(247,206,11,0.32)"}`,
        background: alreadyIn ? "rgba(16,185,129,0.12)" : "rgba(3,78,123,0.18)",
        display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0,
        boxShadow: alreadyIn
          ? "0 0 20px rgba(16,185,129,0.30)"
          : "0 0 18px rgba(247,206,11,0.14)",
      }}>
        {student.photo_url
          ? <img src={`${BASE}/${student.photo_url}`} alt=""
              style={{ width:"100%", height:"100%", objectFit:"cover" }}
              onError={e => { e.currentTarget.style.display="none"; }}/>
          : <span style={{
              fontFamily:T.disp, fontSize:30, fontWeight:700,
              color: alreadyIn ? "#10b981" : "rgba(247,206,11,0.88)",
            }}>{initials}</span>
        }
      </div>

      {/* ID */}
      <div style={{
        fontFamily:T.disp, fontSize:13, fontWeight:700, letterSpacing:"0.10em",
        color: alreadyIn ? "#10b981" : "rgba(255,255,255,0.50)", textAlign:"center",
      }}>
        {student.student_code || `#${student.student_id}`}
      </div>

      {/* CTA */}
      {alreadyIn
        ? <div style={{ marginTop:10, display:"flex", alignItems:"center", gap:5, fontFamily:T.disp, fontSize:11, fontWeight:700, color:"#10b981", letterSpacing:"0.10em" }}>
            <FaCheckCircle size={11}/> CHECKED IN
          </div>
        : <div style={{
            marginTop:10, fontFamily:T.disp, fontSize:12, fontWeight:700, letterSpacing:"0.10em",
            color:T.yellow, background:"rgba(247,206,11,0.10)",
            border:"1px solid rgba(247,206,11,0.30)",
            padding:"5px 14px", borderRadius:20,
          }}>TAP TO CHECK IN</div>
      }
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MAIN KIOSK
// ══════════════════════════════════════════════════════════════════════════════
export default function KioskCheckIn({ currentUser, onLogout }) {
  // When rendered inside LandingPage (no onLogout), use embedded layout
  // (no fixed status bar, no 100vh constraint)
  const embedded = !onLogout;
  const { config } = useAppConfig();
  const logoSrc = config.logo_url || SixersLogo;
  const [batches,     setBatches]     = useState([]);
  const [batchId,     setBatchId]     = useState("");
  const [activeBatch, setActiveBatch] = useState(null);
  const [stats,       setStats]       = useState(null);
  const [query,       setQuery]       = useState("");
  const [results,     setResults]     = useState([]);
  const [searching,   setSearching]   = useState(false);
  const [checking,    setChecking]    = useState(false);
  const [flash,       setFlash]       = useState(null);

  // ── Password-protected logout ────────────────────────────────────────────
  const [showLogout, setShowLogout] = useState(false);
  const [logoutPwd,  setLogoutPwd]  = useState("");
  const [logoutErr,  setLogoutErr]  = useState("");
  const [logoutBusy, setLogoutBusy] = useState(false);
  const pwdRef = useRef(null);

  const inputRef = useRef(null);

  useEffect(() => {
    fetch(`${BASE}/batches/list`)
      .then(r => r.json()).then(d => setBatches(d || [])).catch(() => {});
  }, []);

  const loadStats = useCallback(() => {
    if (!batchId) return;
    fetch(`${BASE}/batches/kiosk/stats?batch_id=${batchId}`)
      .then(r => r.json()).then(d => setStats(d)).catch(() => {});
  }, [batchId]);

  useEffect(() => {
    if (!batchId) { setActiveBatch(null); setStats(null); setResults([]); setQuery(""); return; }
    const b = batches.find(x => String(x.batch_id) === String(batchId));
    setActiveBatch(b || null);
    loadStats();
    inputRef.current?.focus();
  }, [batchId, batches, loadStats]);

  const doSearch = useCallback(async () => {
    if (!query.trim() || !batchId) return;
    setSearching(true);
    try {
      const r = await fetch(`${BASE}/batches/kiosk/search?q=${encodeURIComponent(query)}&batch_id=${batchId}`);
      const d = await r.json();
      setResults(d || []);
    } finally { setSearching(false); }
  }, [query, batchId]);

  useEffect(() => {
    if (!query.trim()) { setResults([]); return; }
    const t = setTimeout(doSearch, 300);
    return () => clearTimeout(t);
  }, [query, doSearch]);

  const handleCheckin = async (student) => {
    if (checking) return;
    setChecking(true);
    try {
      const r = await fetch(`${BASE}/batches/kiosk/checkin`, {
        method:"POST", headers:{"Content-Type":"application/json"},
        body:JSON.stringify({ student_id:student.student_id, batch_id:parseInt(batchId) }),
      });
      if (r.ok) {
        setFlash({ ok:true });
        setQuery(""); setResults([]);
        loadStats();
        setTimeout(() => setFlash(null), 2800);
        inputRef.current?.focus();
      } else {
        setFlash({ ok:false });
        setTimeout(() => setFlash(null), 2800);
      }
    } finally { setChecking(false); }
  };

  // ── Verify password then logout ──────────────────────────────────────────
  const openLogout = () => {
    setLogoutPwd(""); setLogoutErr(""); setShowLogout(true);
    setTimeout(() => pwdRef.current?.focus(), 80);
  };
  const closeLogout = () => { setShowLogout(false); setLogoutPwd(""); setLogoutErr(""); };
  const handleLogoutConfirm = async () => {
    if (!logoutPwd.trim()) { setLogoutErr("Password is required."); return; }
    setLogoutBusy(true); setLogoutErr("");
    try {
      const r = await fetch(`${BASE}/auth/login`, {
        method:"POST", headers:{"Content-Type":"application/json"},
        body:JSON.stringify({ email:currentUser?.email, password:logoutPwd }),
      });
      const d = await r.json();
      if (d.success) {
        onLogout?.();
      } else {
        setLogoutErr("Incorrect password. Access denied.");
        setLogoutPwd("");
        setTimeout(() => pwdRef.current?.focus(), 50);
      }
    } catch {
      setLogoutErr("Unable to reach server. Try again.");
    } finally { setLogoutBusy(false); }
  };

  return (
    <>
      <style>{CSS}</style>

      <div style={{
        minHeight: embedded ? "100%" : "100vh",
        fontFamily:T.font,
        overflow: embedded ? "visible" : "hidden",
        position:"relative",
        background:"#020d18",
        display:"flex", flexDirection:"column", alignItems:"center",
      }}>

        {/* ── Background image — full opacity ────────────────────────────────── */}
        <div style={{
          position:"absolute", inset:0, zIndex:0,
          backgroundImage:`url(${bg2})`,
          backgroundSize:"cover", backgroundPosition:"center 30%",
        }}/>

        {/* Brand-blue tint overlay — shows image clearly while keeping text readable */}
        <div style={{
          position:"absolute", inset:0, zIndex:1,
          background:"linear-gradient(160deg,rgba(2,18,45,0.76) 0%,rgba(3,60,100,0.55) 48%,rgba(2,18,45,0.76) 100%)",
        }}/>

        {/* Yellow top stripe */}
        <div style={{
          position:"absolute", top:0, left:0, right:0, height:3, zIndex:10,
          background:"linear-gradient(90deg,transparent,#f7ce0b 20%,#f7ce0b 80%,transparent)",
          animation:"ki_stripe 1.2s cubic-bezier(.4,0,.2,1) 0.2s both",
        }}/>

        {/* Hex grid */}
        <div style={{ position:"absolute", inset:0, zIndex:2 }}>
          <HexGrid/>
        </div>

        {/* Scan line + CRT lines */}
        <div style={{ position:"absolute", inset:0, zIndex:3, pointerEvents:"none", overflow:"hidden" }}>
          <div style={{
            position:"absolute", left:0, right:0, height:3,
            background:"linear-gradient(180deg,transparent,rgba(247,206,11,0.05),transparent)",
            animation:"ki_scan 9s linear infinite",
          }}/>
          <div style={{
            position:"absolute", inset:0,
            backgroundImage:"repeating-linear-gradient(0deg,transparent,transparent 2px,rgba(0,0,0,0.025) 2px,rgba(0,0,0,0.025) 4px)",
          }}/>
        </div>

        {/* ── Content ────────────────────────────────────────────────────────── */}
        <div style={{
          position:"relative", zIndex:6,
          width:"100%", maxWidth:960,
          padding: embedded ? "32px 24px 32px" : "32px 24px 80px",
          display:"flex", flexDirection:"column", alignItems:"center",
        }}>

          {/* ── Logo + Title ─────────────────────────────────────────────────── */}
          <div style={{
            display:"flex", flexDirection:"column", alignItems:"center",
            marginBottom:32, animation:"ki_fadeUp 0.7s ease 0.15s both",
          }}>
            {/* Logo with spinning rings — consistent with LoginPage */}
            <div style={{ position:"relative", marginBottom:16 }}>
              {/* Outer dashed spinning ring */}
              <div style={{
                position:"absolute", top:-14, left:-14, right:-14, bottom:-14,
                border:"2px dashed rgba(247,206,11,0.30)", borderRadius:"50%",
                animation:"ki_ring 22s linear infinite",
              }}/>
              {/* Inner solid ring */}
              <div style={{
                position:"absolute", top:-6, left:-6, right:-6, bottom:-6,
                border:"1px solid rgba(247,206,11,0.12)", borderRadius:"50%",
              }}/>
              {/* Glow halo behind logo */}
              <div style={{
                position:"absolute", inset:-20,
                borderRadius:"50%",
                background:"radial-gradient(circle, rgba(247,206,11,0.12) 0%, transparent 70%)",
                pointerEvents:"none",
              }}/>
              <img src={logoSrc} alt="Sixers" style={{
                width:118, height:118, objectFit:"contain",
                position:"relative", zIndex:1,
                animation:"ki_pulse 3s ease-in-out infinite",
              }}/>
            </div>

            <h1 style={{
              margin:0,
              fontFamily:T.disp, fontSize:34, fontWeight:700,
              color:"white", letterSpacing:"0.10em", textTransform:"uppercase",
              textShadow:"0 0 40px rgba(247,206,11,0.18)",
            }}>CHECK-IN KIOSK</h1>

            <div style={{ display:"flex", alignItems:"center", gap:10, marginTop:9 }}>
              <div style={{ width:28, height:3, background:T.yellow, borderRadius:2 }}/>
              <span style={{
                fontFamily:T.disp, fontSize:11, fontWeight:700,
                color:T.yellow, letterSpacing:"0.16em",
              }}>TAP YOUR PHOTO TO CHECK IN FOR TODAY'S SESSION</span>
              <div style={{ width:28, height:3, background:T.yellow, borderRadius:2 }}/>
            </div>
          </div>

          {/* ── Batch selector ───────────────────────────────────────────────── */}
          {!batchId && (
            <div style={{
              background:"rgba(4,14,28,0.78)", backdropFilter:"blur(22px)", WebkitBackdropFilter:"blur(22px)",
              border:"1px solid rgba(247,206,11,0.22)",
              borderRadius:20, padding:"28px 32px",
              width:"100%", maxWidth:540,
              animation:"ki_fadeUp 0.6s ease 0.30s both",
              boxShadow:"0 28px 64px rgba(0,0,0,0.50)",
            }}>
              <div style={{
                fontFamily:T.disp, fontSize:14, fontWeight:700,
                color:"rgba(255,255,255,0.65)", marginBottom:16,
                letterSpacing:"0.12em", textTransform:"uppercase",
              }}>Select Today's Batch</div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                {batches.map(b => (
                  <div key={b.batch_id} className="ki_batch"
                    onClick={() => setBatchId(String(b.batch_id))}
                    style={{
                      padding:"18px 16px", borderRadius:14, cursor:"pointer",
                      WebkitTapHighlightColor:"transparent",
                      background:"rgba(255,255,255,0.06)",
                      border:"1.5px solid rgba(247,206,11,0.20)",
                      boxShadow:"0 4px 16px rgba(0,0,0,0.20)",
                    }}>
                    <div style={{ fontFamily:T.disp, fontSize:15, fontWeight:800, color:"white", letterSpacing:"0.04em" }}>{b.batch_name}</div>
                    <div style={{ fontSize:11, color:"rgba(255,255,255,0.48)", marginTop:4, fontFamily:T.font }}>{b.level_name}</div>
                    <div style={{ fontFamily:T.disp, fontSize:12, color:T.yellow, marginTop:6, fontWeight:600, letterSpacing:"0.06em" }}>
                      {fmtTime(b.weekday_start)} – {fmtTime(b.weekday_end)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Active batch area ────────────────────────────────────────────── */}
          {batchId && activeBatch && (
            <div style={{ width:"100%", animation:"ki_fadeUp 0.5s ease 0.1s both" }}>

              {/* Batch info bar */}
              <div style={{
                background:"rgba(4,14,28,0.72)", backdropFilter:"blur(18px)", WebkitBackdropFilter:"blur(18px)",
                border:"1px solid rgba(247,206,11,0.20)",
                borderRadius:16, padding:"14px 22px", marginBottom:22,
                display:"flex", alignItems:"center", justifyContent:"space-between",
                boxShadow:"0 8px 32px rgba(0,0,0,0.30)",
              }}>
                <div>
                  <div style={{ fontFamily:T.disp, fontSize:17, fontWeight:700, color:"white", letterSpacing:"0.06em" }}>{activeBatch.batch_name}</div>
                  <div style={{ fontSize:12, color:"rgba(255,255,255,0.46)", marginTop:2, fontFamily:T.font }}>
                    {activeBatch.level_name} · {fmtTime(activeBatch.weekday_start)} – {fmtTime(activeBatch.weekday_end)}
                  </div>
                </div>
                <button
                  onClick={() => { setBatchId(""); setActiveBatch(null); setResults([]); setQuery(""); }}
                  style={{
                    background:"rgba(247,206,11,0.10)", border:"1px solid rgba(247,206,11,0.30)",
                    color:T.yellow, borderRadius:10, padding:"8px 20px", cursor:"pointer",
                    WebkitTapHighlightColor:"transparent",
                    fontFamily:T.disp, fontSize:12, fontWeight:700, letterSpacing:"0.10em",
                    transition:"background 0.15s",
                  }}>CHANGE BATCH</button>
              </div>

              {/* Stats */}
              {stats && (
                <div style={{ display:"flex", gap:14, justifyContent:"center", marginBottom:24 }}>
                  <Pill icon={FaUsers}       label="Total"      value={stats.total}      color="rgba(255,255,255,0.76)" bg="rgba(255,255,255,0.06)" borderColor="rgba(255,255,255,0.14)"/>
                  <Pill icon={FaCheckCircle} label="Checked In" value={stats.checked_in} color="#10b981"                bg="rgba(16,185,129,0.10)"  borderColor="rgba(16,185,129,0.35)"/>
                  <Pill icon={FaClock}       label="Remaining"  value={stats.remaining}  color={T.yellow}               bg="rgba(247,206,11,0.09)"  borderColor="rgba(247,206,11,0.30)"/>
                </div>
              )}

              {/* ── Check-in flash ─────────────────────────────────────────────── */}
              {flash && (
                <div style={{
                  padding:"24px 36px", borderRadius:16, marginBottom:24,
                  background: flash.ok ? "rgba(16,185,129,0.13)" : "rgba(239,68,68,0.13)",
                  border:`1.5px solid ${flash.ok ? "rgba(16,185,129,0.55)" : "rgba(239,68,68,0.55)"}`,
                  backdropFilter:"blur(18px)", WebkitBackdropFilter:"blur(18px)",
                  display:"flex", alignItems:"center", justifyContent:"center", gap:18,
                  animation:"ki_flash 2.8s ease forwards",
                  boxShadow: flash.ok
                    ? "0 10px 40px rgba(16,185,129,0.22)"
                    : "0 10px 40px rgba(239,68,68,0.18)",
                }}>
                  {flash.ok
                    ? <FaCheckCircle size={32} style={{ color:"#10b981", filter:"drop-shadow(0 0 10px rgba(16,185,129,0.70))", flexShrink:0 }}/>
                    : <FaTimesCircle size={32} style={{ color:"#ef4444", flexShrink:0 }}/>}
                  <div style={{
                    fontFamily:T.disp,
                    fontSize:26, fontWeight:700, letterSpacing:"0.12em",
                    color: flash.ok ? "#10b981" : "#ef4444",
                  }}>
                    {flash.ok ? "CHECK-IN SUCCESSFUL" : "CHECK-IN FAILED"}
                  </div>
                </div>
              )}

              {/* Search */}
              <div style={{ position:"relative", marginBottom:26 }}>
                <FaSearch style={{
                  position:"absolute", left:20, top:"50%", transform:"translateY(-50%)",
                  color:"rgba(247,206,11,0.50)", fontSize:18,
                }}/>
                <input
                  className="ki_search"
                  ref={inputRef}
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  onKeyDown={e => e.key==="Enter" && doSearch()}
                  placeholder="Type student name or ID number…"
                  style={{
                    width:"100%", padding:"18px 18px 18px 56px",
                    borderRadius:16, border:"1.5px solid rgba(247,206,11,0.22)",
                    background:"rgba(4,14,28,0.68)",
                    backdropFilter:"blur(14px)", WebkitBackdropFilter:"blur(14px)",
                    color:"white", fontSize:18, fontFamily:T.font,
                    boxSizing:"border-box", outline:"none",
                    boxShadow:"0 4px 28px rgba(0,0,0,0.32)",
                    transition:"border-color 0.2s, box-shadow 0.2s",
                  }}/>
                {searching && (
                  <div style={{
                    position:"absolute", right:20, top:"50%", transform:"translateY(-50%)",
                    width:18, height:18, borderRadius:"50%",
                    border:"2.5px solid rgba(247,206,11,0.22)", borderTopColor:T.yellow,
                    animation:"ki_spin 0.7s linear infinite",
                  }}/>
                )}
              </div>

              {/* Results grid */}
              {results.length > 0 && (
                <div style={{ display:"flex", gap:16, flexWrap:"wrap", justifyContent:"center" }}>
                  {results.map(s => (
                    <StudentCard key={s.student_id} student={s} onCheckin={handleCheckin} loading={checking}/>
                  ))}
                </div>
              )}

              {/* No results */}
              {query && results.length === 0 && !searching && (
                <div style={{ textAlign:"center", color:"rgba(255,255,255,0.38)", fontSize:15, padding:40, fontFamily:T.font }}>
                  No students found matching "{query}" in this batch.
                </div>
              )}

              {/* Idle prompt */}
              {!query && (
                <div style={{ textAlign:"center", padding:"52px 24px" }}>
                  <FaSearch size={38} style={{ color:"rgba(247,206,11,0.15)", display:"block", margin:"0 auto 14px" }}/>
                  <p style={{ color:"rgba(255,255,255,0.28)", fontSize:14, fontFamily:T.font, margin:0, letterSpacing:"0.03em" }}>
                    Start typing a name or ID to search
                  </p>
                </div>
              )}
            </div>
          )}

          {/* ── Privacy notice ───────────────────────────────────────────────── */}
          <div style={{ marginTop:44, width:"100%", display:"flex", justifyContent:"center" }}>
            <div style={{
              display:"flex", alignItems:"flex-start", gap:13,
              background:"rgba(4,14,28,0.62)",
              backdropFilter:"blur(14px)", WebkitBackdropFilter:"blur(14px)",
              border:"1px solid rgba(255,255,255,0.08)",
              borderRadius:12, padding:"12px 22px",
              width:"100%", maxWidth:900,
              boxShadow:"0 4px 24px rgba(0,0,0,0.22)",
            }}>
              <FaShieldAlt size={15} style={{ color:"rgba(247,206,11,0.48)", marginTop:2, flexShrink:0 }}/>
              <div style={{ fontSize:11, lineHeight:1.75, color:"rgba(255,255,255,0.32)", fontStyle:"italic", fontFamily:T.font }}>
                <strong style={{ fontStyle:"normal", fontWeight:700, color:"rgba(255,255,255,0.50)" }}>Privacy Notice: </strong>
                Participant names are not displayed on this kiosk in compliance with applicable privacy and data-protection obligations.<br/>
                Identity is verified solely through the assigned student ID and photo. Unauthorised access to another individual's record is strictly prohibited.
              </div>
            </div>
          </div>

        </div>{/* /content */}

        {/* ── Fixed status bar — full-screen kiosk only ────────────────────────── */}
        {!embedded && <div style={{
          position:"fixed", bottom:0, left:0, right:0, zIndex:10,
          height:34, display:"flex", alignItems:"center",
          padding:"0 20px", gap:16,
          background:"rgba(0,0,0,0.75)",
          borderTop:"1px solid rgba(247,206,11,0.09)",
          backdropFilter:"blur(12px)", WebkitBackdropFilter:"blur(12px)",
        }}>
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            <div style={{ width:7, height:7, borderRadius:"50%", background:"#4ade80", animation:"ki_dot 2s ease-in-out infinite" }}/>
            <span style={{ fontFamily:T.disp, fontSize:11, color:"rgba(255,255,255,0.38)", letterSpacing:"0.12em" }}>KIOSK ONLINE</span>
          </div>
          <div style={{ flex:1, height:1, background:"rgba(255,255,255,0.05)" }}/>
          <span style={{ fontFamily:T.disp, fontSize:11, color:"rgba(255,255,255,0.55)", letterSpacing:"0.08em" }}>
            SIXERS SPORTS CENTER · SECURE CHECK-IN KIOSK
          </span>
          {/* Staff-only logout — subtle but accessible */}
          {onLogout && (
            <button onClick={openLogout} style={{
              marginLeft:12, display:"flex", alignItems:"center", gap:6,
              background:"rgba(239,68,68,0.10)", border:"1px solid rgba(239,68,68,0.28)",
              color:"rgba(239,68,68,0.70)", borderRadius:7, padding:"4px 12px",
              cursor:"pointer", fontFamily:T.disp, fontSize:10, fontWeight:700,
              letterSpacing:"0.10em", WebkitTapHighlightColor:"transparent",
              transition:"background 0.15s",
            }}>
              <FaSignOutAlt size={10}/> STAFF EXIT
            </button>
          )}
        </div>}

        {/* ── Password-protected logout modal ──────────────────────────────────── */}
        {showLogout && (
          <div style={{
            position:"fixed", inset:0, zIndex:200,
            background:"rgba(2,13,24,0.88)",
            backdropFilter:"blur(16px)", WebkitBackdropFilter:"blur(16px)",
            display:"flex", alignItems:"center", justifyContent:"center",
          }}
            onClick={e => { if (e.target === e.currentTarget) closeLogout(); }}>
            <div style={{
              background:"rgba(4,14,28,0.95)",
              border:"1px solid rgba(247,206,11,0.30)",
              borderRadius:18, padding:"36px 40px",
              width:"100%", maxWidth:400,
              boxShadow:"0 32px 80px rgba(0,0,0,0.70)",
              position:"relative",
              animation:"ki_fadeUp 0.25s ease both",
            }}>
              {/* Yellow corner accents */}
              {[["top:0,left:0","top","left"],["top:0,right:0","top","right"],["bottom:0,left:0","bottom","left"],["bottom:0,right:0","bottom","right"]].map(([_,v,h],i)=>(
                <div key={i} style={{ position:"absolute", [v]:-1, [h]:-1, width:22, height:22 }}>
                  <div style={{ position:"absolute", [v]:0, [h]:0, width:22, height:2.5, background:T.yellow, borderRadius:2 }}/>
                  <div style={{ position:"absolute", [v]:0, [h]:0, width:2.5, height:22, background:T.yellow, borderRadius:2 }}/>
                </div>
              ))}

              {/* Icon + heading */}
              <div style={{ textAlign:"center", marginBottom:24 }}>
                <div style={{
                  width:56, height:56, borderRadius:"50%", margin:"0 auto 14px",
                  background:"rgba(247,206,11,0.10)", border:"1px solid rgba(247,206,11,0.30)",
                  display:"flex", alignItems:"center", justifyContent:"center",
                }}>
                  <FaShieldAlt size={24} style={{ color:T.yellow }}/>
                </div>
                <div style={{ fontFamily:T.disp, fontSize:22, fontWeight:700, color:"white", letterSpacing:"0.08em" }}>
                  SECURE LOGOUT
                </div>
                <div style={{ fontSize:12, color:"rgba(255,255,255,0.42)", marginTop:6, fontFamily:T.font }}>
                  Enter your password to end this kiosk session
                </div>
              </div>

              {/* Error */}
              {logoutErr && (
                <div style={{
                  padding:"10px 16px", borderRadius:9, marginBottom:16,
                  background:"rgba(239,68,68,0.12)", border:"1px solid rgba(239,68,68,0.40)",
                  color:"#fca5a5", fontSize:13, fontFamily:T.font, display:"flex", alignItems:"center", gap:8,
                }}>
                  <FaTimesCircle size={13}/>{logoutErr}
                </div>
              )}

              {/* Password input */}
              <div style={{ position:"relative", marginBottom:20 }}>
                <FaLock style={{
                  position:"absolute", left:16, top:"50%", transform:"translateY(-50%)",
                  color:"rgba(247,206,11,0.45)", fontSize:14,
                }}/>
                <input
                  ref={pwdRef}
                  type="password"
                  value={logoutPwd}
                  onChange={e => { setLogoutPwd(e.target.value); setLogoutErr(""); }}
                  onKeyDown={e => e.key==="Enter" && handleLogoutConfirm()}
                  placeholder="Enter your password…"
                  style={{
                    width:"100%", padding:"14px 16px 14px 48px",
                    borderRadius:12, border:"1.5px solid rgba(247,206,11,0.25)",
                    background:"rgba(255,255,255,0.05)",
                    color:"white", fontSize:16, fontFamily:T.font,
                    boxSizing:"border-box", outline:"none",
                  }}
                />
              </div>

              {/* Buttons */}
              <div style={{ display:"flex", gap:12 }}>
                <button onClick={closeLogout} style={{
                  flex:1, padding:"13px", borderRadius:10,
                  border:"1px solid rgba(255,255,255,0.15)",
                  background:"rgba(255,255,255,0.06)", color:"rgba(255,255,255,0.65)",
                  fontFamily:T.disp, fontSize:13, fontWeight:700, letterSpacing:"0.08em",
                  cursor:"pointer",
                }}>CANCEL</button>
                <button onClick={handleLogoutConfirm} disabled={logoutBusy} style={{
                  flex:1, padding:"13px", borderRadius:10, border:"none",
                  background:logoutBusy ? "rgba(247,206,11,0.40)" : "linear-gradient(135deg,#f7ce0b,#e8b800)",
                  color:"#02304f", fontFamily:T.disp, fontSize:13, fontWeight:700, letterSpacing:"0.08em",
                  cursor:logoutBusy ? "default" : "pointer",
                  display:"flex", alignItems:"center", justifyContent:"center", gap:8,
                }}>
                  {logoutBusy ? "VERIFYING…" : <><FaSignOutAlt size={13}/>CONFIRM EXIT</>}
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </>
  );
}
